import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { App } from './App';
import { RootErrorBoundary } from '@/components/errors/root-error-boundary';
import { env } from '@/lib/env';
import { buildSentryInitOptions } from '@/lib/observability/sentry-init';
import '@/styles/globals.css';

// Land every refresh at the top of the page. The browser default (`auto`) re-applies
// the pre-reload scroll position, but the app's routes load their content async — so
// it restores against still-loading/shorter content and can leave a route (e.g. the
// mailbox inbox) scrolled to the bottom on refresh. `manual` opts out; SPA navigation
// already starts at the top.
if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

if (env.sentry.dsn !== undefined) Sentry.init(buildSentryInitOptions(env.sentry.dsn));

// Pages load on demand, so a tab left open across a deploy asks for page files the new release no longer serves.
// Reload once onto the new release; the stored mark stops a reload loop when the file is missing for another reason.
const PRELOAD_RELOAD_KEY = 'immo:preload-error-reload';

window.addEventListener('vite:preloadError', (event) => {
  try {
    if (sessionStorage.getItem(PRELOAD_RELOAD_KEY) !== null) return;

    sessionStorage.setItem(PRELOAD_RELOAD_KEY, '1');
  } catch {
    return;
  }

  event.preventDefault();
  window.location.reload();
});

window.addEventListener('load', () => {
  try {
    sessionStorage.removeItem(PRELOAD_RELOAD_KEY);
  } catch {
    // Storage blocked: the reload guard above already bails out.
  }
});

const container = document.getElementById('root');

if (!container) throw new Error('Missing #root element in index.html');

createRoot(container).render(
  <StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </StrictMode>,
);
